import { MouseEventHandler, useCallback, useContext, useEffect, useMemo } from 'react';
import { applyNodeChanges, NodePositionChange, useReactFlow, XYPosition } from '@xyflow/react';
import {
  GroupNodeBoundaries,
  RfNodeGroup,
  RfNodeGroupLabel,
  RfNodeStandalone,
  RfNodeStickyNote,
  SweepCanvasRfNode,
} from './canvas-types/nodeTypesData';
import { SWEEP_GRID_SIZE } from './const';
import {
  convertGridPositionToXYPosition,
  convertXYPositionToGridIndex,
} from './helpers/gridPositioningUtils';
import { useSweepCanvasPropsCtx } from './internal-context/SweepCanvasPropsCtx';
import { useSweepCanvasState } from './internal-context/CanvasStateContext';
import { createObjectOperations } from './node-changes-event-handlers/objectOperations';
import { calculateGroupLabelPosition } from './helpers/calculateGroupLabelPosition';
import { CanvasWrapperResizeObserverContext } from './internal-context/CanvasWrapperDimensionsContext';
import usePrevious from '../../hooks/usePrevious';
import { CanvasElementType } from './canvas-types/canvasTypes';
import { useGetIntersectionSection } from './sections/useGetIntersectionSection';
import {
  clearSectionIsDropTargetProperty,
  updateSectionIsDropTargetProperty,
} from './sections/updateSectionIsDropTargetProperty';

import {
  NodeMoveEvent,
  useSectionableNodeMoveEvent,
} from './node-changes-event-handlers/position-events/useSectionableNodeMoveEvent';

export interface CanvasNodesMouseMove {
  nodeIds: string[];
  onDrop: (moveEvent: NodeMoveEvent) => void;
}

// This hook calculates the position of a group node based on mouse coordinates, taking into account the grid size and zoom level
// It returns the position of the group node in the canvas grid and the position in the react flow
const useCalcGroupPosFromMouse = () => {
  const { getZoom, screenToFlowPosition } = useReactFlow();

  return useCallback(
    ({ minCol, minRow }: GroupNodeBoundaries, currentMousePosition: XYPosition) => {
      const zoom = getZoom();
      const position = {
        x: currentMousePosition.x - minCol * SWEEP_GRID_SIZE.width * zoom,
        y: currentMousePosition.y + minRow * SWEEP_GRID_SIZE.height * zoom,
      };

      const xyPosition = screenToFlowPosition(position);
      const canvasGridPosition = convertXYPositionToGridIndex(xyPosition, {
        withPaddingByNodeType: CanvasElementType.GROUP,
      });
      return { canvasGridPosition, xyPosition };
    },
    [getZoom, screenToFlowPosition],
  );
};

export function useCanvasNodesMouseMove() {
  const { canvasNodesMouseMove } = useSweepCanvasPropsCtx();
  const { setCanvasNodes, getCanvasNodes } = useSweepCanvasState();
  const { sweepGroups, sweepStandaloneNodes, sweepStickyNotesNodes } = useSweepCanvasPropsCtx();
  const { getIntersectionSection } = useGetIntersectionSection();
  const { createSectionableNodeMoveEvent } = useSectionableNodeMoveEvent();
  const { screenToFlowPosition } = useReactFlow();

  const calcGroupPosFromMouse = useCalcGroupPosFromMouse();
  const { canvasWrapperRef } = useContext(CanvasWrapperResizeObserverContext);

  const moveGroupsToPosition = useCallback(
    (screenPosition: XYPosition, canvasNodesMouseMove: CanvasNodesMouseMove) => {
      setCanvasNodes((nodes) => {
        const nodeOperations = createObjectOperations<SweepCanvasRfNode>(nodes);
        const { getObject: getNode, setObject: setNode, getObjects: getAllNodes } = nodeOperations;

        canvasNodesMouseMove.nodeIds.forEach((nodeId) => {
          const sweepNode =
            sweepGroups.find((node) => node.id === nodeId) ||
            sweepStandaloneNodes?.find((node) => node.id === nodeId) ||
            sweepStickyNotesNodes?.find((node) => node.id === nodeId);
          if (!sweepNode) return;
          const sweepNodeXYPosition =
            sweepNode?.nodeType === 'stickyNote'
              ? sweepNode.xyPosition
              : convertGridPositionToXYPosition(sweepNode.position, {
                  withPaddingByNodeType: CanvasElementType.GROUP,
                });

          const node = getNode<RfNodeGroup | RfNodeStandalone | RfNodeStickyNote>(nodeId);
          if (!node) return;

          const intersectionSectionNode = getIntersectionSection(node);

          updateSectionIsDropTargetProperty({
            intersectionSectionNode,
            dragging: true,
            nodeOperations,
            nodeType: node.type,
          });

          if (node.type === CanvasElementType.GROUP) {
            const labelGroupNode = getNode<RfNodeGroupLabel>(`label-${nodeId}`);

            if (node && labelGroupNode && sweepNode) {
              const { xyPosition } = calcGroupPosFromMouse(
                node.data.nodeBoundaries,
                screenPosition,
              );

              const groupPosition = {
                x: xyPosition.x + sweepNodeXYPosition.x,
                y: xyPosition.y + sweepNodeXYPosition.y,
              };

              setNode<RfNodeGroup>(nodeId, {
                ...node,
                position: groupPosition,
              });

              setNode<RfNodeGroupLabel>(`label-${nodeId}`, {
                ...labelGroupNode,
                position: calculateGroupLabelPosition(groupPosition, node.data.boundingBox),
              });
              return;
            }
          }

          const xyPosition = screenToFlowPosition({
            x: screenPosition.x,
            y: screenPosition.y,
          });

          setNode(nodeId, {
            ...node,
            position: {
              x: xyPosition.x + sweepNodeXYPosition.x + 60,
              y: xyPosition.y + sweepNodeXYPosition.y,
            },
          });
        });
        return getAllNodes();
      });
    },
    [
      setCanvasNodes,
      sweepGroups,
      sweepStandaloneNodes,
      sweepStickyNotesNodes,
      getIntersectionSection,
      screenToFlowPosition,
      calcGroupPosFromMouse,
    ],
  );

  const onMoveGroupsMouseMoveHandler: MouseEventHandler<HTMLDivElement> = useCallback(
    (e) => {
      if (!canvasNodesMouseMove) {
        return;
      }
      moveGroupsToPosition(
        {
          x: e.clientX,
          y: e.clientY,
        },
        canvasNodesMouseMove,
      );
    },
    [canvasNodesMouseMove, moveGroupsToPosition],
  );

  const handleOnGroupsDropClick: MouseEventHandler<HTMLDivElement> = useCallback(
    (e) => {
      if (!canvasNodesMouseMove) {
        return;
      }
      const currentMousePosition = {
        x: e.clientX,
        y: e.clientY,
      };
      const nodeOperations = createObjectOperations<SweepCanvasRfNode>(getCanvasNodes());
      const { getObject: getOperationsNode } = nodeOperations;

      clearSectionIsDropTargetProperty(nodeOperations);

      const groupNode = getOperationsNode<RfNodeGroup | RfNodeStandalone>(
        canvasNodesMouseMove.nodeIds[0],
      );
      if (!groupNode) return;

      const intersectionSectionNode = getIntersectionSection(groupNode);

      const nodeBoundaries =
        groupNode.type === CanvasElementType.GROUP
          ? groupNode.data.nodeBoundaries
          : {
              minCol: 0,
              minRow: 0,
              maxCol: 0,
              maxRow: 0,
            };

      const { xyPosition } = calcGroupPosFromMouse(nodeBoundaries, currentMousePosition);
      const nodeChange: NodePositionChange = {
        id: groupNode.id,
        position: xyPosition,
        type: 'position',
        dragging: false,
      };
      const node = getOperationsNode<RfNodeGroup | RfNodeStandalone>(groupNode.id);
      if (!node) return null;

      const moveEvent = createSectionableNodeMoveEvent({
        nodeChange,
        node,
        nodeOperations,
        intersectionSectionNode,
      });
      if (!moveEvent) {
        return null;
      }

      setCanvasNodes(applyNodeChanges([nodeChange], nodeOperations.getObjects()));

      canvasNodesMouseMove.onDrop(moveEvent);
    },
    [
      canvasNodesMouseMove,
      getCanvasNodes,
      getIntersectionSection,
      calcGroupPosFromMouse,
      createSectionableNodeMoveEvent,
      setCanvasNodes,
    ],
  );

  // Before the mouse move event is triggered, we need to ensure the groups are in the center of the canvas
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    if (canvasNodesMouseMove?.nodeIds && canvasWrapperRef.current) {
      // viewport center coordinates
      const boundingRect = canvasWrapperRef.current.getBoundingClientRect();
      const viewportCenter = {
        x: boundingRect.left + boundingRect.width / 2,
        y: boundingRect.top + boundingRect.height / 2,
      };

      // We need a small timeout to ensure the DOM has updated and the viewport center calculation is accurate
      // Without this, the initial position of dragged groups may be incorrect
      timeoutId = setTimeout(() => {
        moveGroupsToPosition(
          {
            x: viewportCenter.x,
            y: viewportCenter.y,
          },
          canvasNodesMouseMove,
        );
      }, 5);
    }
    return () => {
      timeoutId && clearTimeout(timeoutId);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canvasNodesMouseMove?.nodeIds]);

  const previousMoveGroupIds = usePrevious(canvasNodesMouseMove?.nodeIds ?? null);

  // When the move groups are removed, we need to remove the nodes that are no longer in the move groups
  // This is to ensure that the nodes are not left behind when the move groups are removed
  useEffect(() => {
    if (!canvasNodesMouseMove?.nodeIds && previousMoveGroupIds) {
      setCanvasNodes((nodes) => {
        return nodes.filter((node) => {
          if (previousMoveGroupIds.includes(node.id.replace('label-', ''))) {
            return false;
          }
          if (node.parentId && previousMoveGroupIds.includes(node.parentId)) {
            return false;
          }
          return true;
        });
      });
    }
  }, [canvasNodesMouseMove?.nodeIds, previousMoveGroupIds, setCanvasNodes]);

  return useMemo(() => {
    if (!canvasNodesMouseMove) {
      return {
        handleOnGroupsDropClick: undefined,
        onMoveGroupsMouseMoveHandler: undefined,
      };
    }
    return {
      handleOnGroupsDropClick,
      onMoveGroupsMouseMoveHandler,
    };
  }, [handleOnGroupsDropClick, canvasNodesMouseMove, onMoveGroupsMouseMoveHandler]);
}
